import React, { useState, useRef, useEffect, useCallback } from 'react';
import cx from 'classnames';
import { MdClose } from 'react-icons/md';
import debounce from 'lodash/debounce';
import { Modal, ModalProps } from '../Modal';
import { TextInput } from '../TextInput';

interface SearchSelectProps<T> {
  initialSearchText?: string;
  // The currently selected option
  value: T | null;
  // Function to load the options data with search text
  loadOptions: (searchText: string) => Promise<T[]> | T[];
  // Function to render each option in the dropdown
  renderOption: (option: T, selectCallback: () => void, isSelected: boolean) => React.ReactNode;
  // Function to render the selected option
  renderSelected: (option: T) => React.ReactNode;
  // Callback when an option is selected
  onSelect: (option: T) => void;
  // Callback when selection is cleared
  onClear?: () => void;
  // Placeholder text when no option is selected
  placeholder?: string;
  // Label for the select
  label?: string;
  // Optional className for styling
  className?: string;
  // Optional width for styling
  modalWidth?: ModalProps['width'];
  // Optional disabled state
  disabled?: boolean;
  // Optional search input
  withSearch?: boolean;
  // Allow delete
  allowDelete?: boolean;
}

export function SearchSelect<T>({
  initialSearchText,
  value,
  loadOptions: loadOptionsProp,
  renderOption,
  renderSelected,
  onSelect,
  onClear,
  placeholder = 'Select an option',
  label,
  className = '',
  disabled = false,
  modalWidth = 'medium',
  withSearch = true,
  allowDelete = true,
}: SearchSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [searchText, setSearchText] = useState(initialSearchText || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const loadOptions = useCallback(async (text: string) => {
    setIsLoading(true);
    const result = await loadOptionsProp(text);
    setOptions(result);
    setIsLoading(false);
    return result;
  }, [loadOptionsProp]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((text: string) => {
      setIsLoading(true);

      const result = loadOptions(text);
      if (result instanceof Promise) {
        result.then((data) => {
          setOptions(data);
        })
        .catch((error) => {
          console.error('Error loading options:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
      } else {
        setOptions(result);
      }

      setIsLoading(false);
    }, 300)
  , [loadOptions]);

  // Load options when search text changes
  useEffect(() => {
    if (isOpen) {
      debouncedSearch(searchText);
    }
  }, [searchText, isOpen, debouncedSearch]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClear) {
      onClear();
    }
  };

  const handleOptionSelect = (option: T) => {
    onSelect(option);
    setIsOpen(false);
    setSearchText('');
  };

  const handleOpenModal = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      {/* Selected option or search input */}
      <div
        className={cx(
          'bg-white/80 flex items-center justify-between p-2 border rounded border-gray-400',
          !disabled && 'hover:border-blue-600 cursor-pointer',
          disabled ? 'opacity-70 cursor-not-allowed' : ''
        )}
        onClick={handleOpenModal}
      >
        {value ? (
          <div className="flex items-center justify-between w-full">
            {renderSelected(value)}
            {allowDelete && (
              <button
                onClick={handleClear}
                className="ml-2 p-1 hover:bg-gray-100 rounded-full"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>
        ) : (
          <div className="w-full text-gray-500">
            {placeholder}
          </div>
        )}
        <svg
          className={`w-6 h-6 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Options Modal */}
      {isOpen && (
        <Modal
          onClose={() => setIsOpen(false)}
          width={modalWidth || 'medium'}
          noContentPadding
        >
            {/* Search Input */}
            {withSearch && (
              <div className="p-2">
                <TextInput
                  ref={inputRef}
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search options..."
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            )}

            {/* Options List */}
            <div className="overflow-y-auto py-1">
              {isLoading && (
                <div className="flex items-center justify-center p-8">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
              )}
              {!isLoading && options.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500">
                  {searchText ? 'No options found' : 'Start typing to search options'}
                </div>
              )} 
              {!isLoading && options.length > 0 && (options.map((option, index) => (
                renderOption(
                  option,
                  () => handleOptionSelect(option),
                  (option === value ? true : false)
                )
              )))}
          </div>
        </Modal>
      )}
    </div>
  );
} 